import React, { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth, API } from "../context/AuthContext";
import ProjectDialog from "../components/ProjectDialog";
import { SLOTS, STATUS_LABEL } from "../lib/slots";
import { fmtDateDE } from "../lib/finance";
import { extendEndByVisibleNotes, buildExemptRefs } from "../lib/projectDates";
import { ChevronLeft } from "lucide-react";

function formatErr(detail) {
  if (!detail) return "Aktion fehlgeschlagen.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function ProjectDetailPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/projects`);
      const found = (res.data || []).find((p) => p.id === id);
      if (!found) {
        setError("Projekt nicht gefunden.");
        setProject(null);
      } else {
        setProject(found);
      }
    } catch (e) {
      setError(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { if (user) load(); }, [user, load]);

  const handleBack = () => {
    const from = location.state?.from;
    if (from?.view) { navigate("/", { state: { restore: from } }); return; }
    if (window.history.length > 1) navigate(-1);
    else navigate("/");
  };

  const handleSave = async (form) => {
    // Same milestone move safeguards as PlannerPage.handleSave, minus the
    // planning-note warning dialog (planning notes can only be edited here).
    if (!form.id) return;
    try {
      if (form.milestone_date) {
        if (form.milestone_date < form.start_date) {
          toast.error("Meilenstein liegt vor dem Projekt-Startdatum.");
          return;
        }
        if (form.milestone_date > form.end_date) form.end_date = form.milestone_date;
      }
      // Sichtbare Planungsnotizen (Planer-Häkchen) verlängern das Projektende, nie verkürzen.
      form.end_date = extendEndByVisibleNotes(form.end_date, form.planning_notes, buildExemptRefs(form));
      // eslint-disable-next-line no-unused-vars
      const { id: pid, created_at: _createdAt, ...payload } = form;
      void _createdAt;
      await axios.put(`${API}/projects/${pid}`, payload);
      toast.success("Projekt aktualisiert");
      // Close the detail page and return to the previous view — same
      // navigation as the Zurück button. Fallback to / when there is no
      // usable previous route (direct load / refresh).
      handleBack();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    }
  };

  const handleDelete = async (pid) => {
    if (!pid) return;
    if (!window.confirm("Projekt wirklich löschen? Dies kann nicht rückgängig gemacht werden.")) return;
    try {
      await axios.delete(`${API}/projects/${pid}`);
      toast.success("Projekt gelöscht");
      navigate("/");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50 text-sm text-slate-500">
        Projekt wird geladen…
      </div>
    );
  }
  if (error || !project) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center gap-3 px-6">
        <div className="text-sm text-slate-600">{error || "Projekt nicht gefunden."}</div>
        <button
          type="button"
          onClick={() => navigate("/")}
          className="text-xs uppercase tracking-widest text-slate-700 hover:text-slate-900 underline underline-offset-4"
        >
          Zurück zum Planer
        </button>
      </div>
    );
  }

  const slot = SLOTS[project.slot - 1];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="project-detail-page">
      <div className="mx-auto w-full max-w-6xl px-6 pt-6">
        <button
          type="button"
          onClick={handleBack}
          className="inline-flex items-center gap-1 text-[11px] tracking-[0.16em] uppercase text-slate-500 hover:text-slate-900"
          data-testid="project-detail-back-top"
        >
          <ChevronLeft className="w-3.5 h-3.5" />
          Zurück
        </button>

        <div className="mt-4 rounded-md border border-slate-200 bg-white px-6 py-5">
          <div className="flex items-start justify-between gap-6 flex-wrap">
            <div className="min-w-0">
              <div className="text-[10px] tracking-[0.2em] uppercase text-slate-500 mb-1">
                Projekt-Karteikarte
              </div>
              <div
                className="text-2xl tracking-tight font-semibold text-slate-900 truncate"
                style={{ fontFamily: "Manrope, ui-sans-serif" }}
                data-testid="project-detail-heading"
              >
                {project.name}
              </div>
              <div className="mt-1 text-xs text-slate-500 truncate">
                {project.client || <span className="text-slate-300">—</span>}
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-1 text-xs">
              <MetaField label="Slot">
                <span className="inline-flex items-center gap-1.5">
                  <span
                    className="w-2.5 h-2.5 rounded-sm border border-black/10"
                    style={{ backgroundColor: slot?.color }}
                  />
                  <span className="tabular-nums text-slate-700">#{project.slot}</span>
                </span>
              </MetaField>
              <MetaField label="Status">{STATUS_LABEL[project.status] || project.status}</MetaField>
              <MetaField label="Kürzel">
                {project.planner_short_label || <span className="text-slate-300">—</span>}
              </MetaField>
              <MetaField label="Start">{fmtDateDE(project.start_date)}</MetaField>
              <MetaField label="Ende">{fmtDateDE(project.end_date)}</MetaField>
              <MetaField label="Meilenstein">
                {project.milestone_date ? fmtDateDE(project.milestone_date) : <span className="text-slate-300">—</span>}
              </MetaField>
            </div>
          </div>
        </div>
      </div>

      <ProjectDialog
        variant="page"
        open={true}
        onOpenChange={() => {}}
        initial={project}
        onSave={handleSave}
        onDelete={handleDelete}
        onBack={handleBack}
      />
    </div>
  );
}

function MetaField({ label, children }) {
  return (
    <div className="min-w-0">
      <div className="text-[9px] tracking-[0.18em] uppercase text-slate-400">{label}</div>
      <div className="text-slate-700 truncate">{children}</div>
    </div>
  );
}
